import {
  CCol,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CTimePicker,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import FixtureService from 'src/service/FixtureService'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'

const EditForm = (props) => {
  const [fixtureDetail, setFixtureDetail] = useState(null)
  const [teamList, setTeamList] = useState([])
  const [matchTypeList, setMatchTypeList] = useState([])

  // UI state (CoreUI expects these formats)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')

  const [loader, setLoader] = useState(false)

  /* =========================
     FETCH FIXTURE (EDIT MODE)
  ========================== */
  useEffect(() => {
    if (props.selectedId !== props.fixtureId) return

    FixtureService.getFixtureDetailOld(props.fixtureId).then((res) => {
      if (res.status === 200) {
        const data = res.data
        setFixtureDetail(data)

        // Dates → Date objects
        setStartDate(data.start_date ? new Date(data.start_date) : null)
        setEndDate(data.end_date ? new Date(data.end_date) : null)

        // Times → HH:mm
        setStartTime(data.start_time || '')
        setEndTime(data.end_time || '')

        TeamService.getTeamListByGrade(data.grade).then((teamRes) => {
          if (teamRes.status === 200) {
            setTeamList(teamRes.data || [])
            setMatchTypeList(teamRes.matchTypeList || [])
          }
        })
      }
    })
  }, [props.selectedId, props.fixtureId])

  /* =========================
     VALIDATION
  ========================== */
  const validationSchema =
    props.gameType === 1
      ? Yup.object({
          team: Yup.string().required('Team is required'),
        })
      : Yup.object({
          team: Yup.string().required('Home team is required'),
          team2: Yup.string().required('Away team is required'),
        })

  /* =========================
     FORMIK
  ========================== */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      team: fixtureDetail?.team || '',
      team2: fixtureDetail?.team2 || '',
      match_type: fixtureDetail?.match_code || '',
      vanue: fixtureDetail?.vanue || '',
      opposition_club: fixtureDetail?.opposition_club || '',
      start_date: fixtureDetail?.start_date || '',
      end_date: fixtureDetail?.end_date || '',
      start_time: fixtureDetail?.start_time || '',
      end_time: fixtureDetail?.end_time || '',
    },
    validationSchema,
    onSubmit: (values) => {
      setLoader(true)

      const payload = {
        ...values,
        fixtureId: fixtureDetail.id,
        start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
        end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        start_time: startTime,
        end_time: endTime,
      }

      FixtureService.editFixture(payload)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            props.fetchData(
              props.activePage,
              props.columnFilter,
              props.columnSorter,
              props.itemsPerPage,
            )
            props.toggleDetails(fixtureDetail.id)
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  /* =========================
     RENDER
  ========================== */
  return (
    <CForm className="row g-3" onSubmit={formik.handleSubmit}>
      {/* TEAM */}
      <CCol md={3}>
        <CFormLabel className="fw-bold">Team *</CFormLabel>
        <CFormSelect
          name="team"
          value={formik.values.team}
          onChange={formik.handleChange}
          className={formik.touched.team && formik.errors.team ? 'is-invalid' : ''}
        >
          <option value="">Select Team</option>
          {teamList.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </CFormSelect>
        <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
      </CCol>

      {/* START DATE */}
      <CCol md={3}>
        <CFormLabel className="fw-bold">Start Date *</CFormLabel>
        <CDatePicker
          date={startDate}
          format="dd/MM/yyyy"
          placeholder="Start Date"
          cleaner={false}
          onDateChange={(date) => {
            setStartDate(date)
            setEndDate(null)
            formik.setFieldTouched('start_date', true)
          }}
        />
      </CCol>

      {/* END DATE */}
      <CCol md={3}>
        <CFormLabel className="fw-bold">End Date *</CFormLabel>
        <CDatePicker
          date={endDate}
          minDate={startDate}
          format="dd/MM/yyyy"
          placeholder="End Date"
          cleaner={false}
          onDateChange={(date) => {
            setEndDate(date)
            formik.setFieldTouched('end_date', true)
          }}
        />
      </CCol>

      {/* START TIME (AM/PM) */}
      <CCol md={3}>
        <CFormLabel className="fw-bold">Start Time *</CFormLabel>
        <CTimePicker
          time={startTime}
          ampm
          seconds={false}
          cleaner={false}
          locale="en-US"
          onTimeChange={(t) => {
            const time24 = moment(t, ['hh:mm A', 'HH:mm']).format('HH:mm')
            setStartTime(time24)
            formik.setFieldTouched('start_time', true)
          }}
        />
      </CCol>

      {/* END TIME (AM/PM) */}
      <CCol md={3}>
        <CFormLabel className="fw-bold">End Time *</CFormLabel>
        <CTimePicker
          time={endTime}
          ampm
          seconds={false}
          cleaner={false}
          locale="en-US"
          onTimeChange={(t) => {
            const time24 = moment(t, ['hh:mm A', 'HH:mm']).format('HH:mm')
            setEndTime(time24)
            formik.setFieldTouched('end_time', true)
          }}
        />
      </CCol>

      {/* SUBMIT */}
      <CCol md={12}>
        {fixtureDetail?.status !== 2 && (
          <CLoadingButton type="submit" color="success" loading={loader}>
            Update Fixture
          </CLoadingButton>
        )}
      </CCol>
    </CForm>
  )
}

export default EditForm
